import { useRouter } from 'next/router'
import React from 'react'

import { SEO_DEFAULT_LOCALE, localePrefix } from './constants'
import type { SeoMeta } from './types'

/**
 * The canonical url and the hreflang set for an SEO page.
 *
 * Returns an array rather than rendering a component, because next/head only reaches
 * elements that are its direct children, in an array, or in a single fragment — a
 * component nested inside it survives the server render and then breaks on client
 * navigation, where the head manager calls document.createElement on the element type.
 *
 * Every category is reachable under both its Swedish url and the English route it rewrites
 * to, so each page has to name which one counts. Produces nothing without NEXT_PUBLIC_HOST,
 * since a canonical has to be absolute to mean anything.
 */
export const useSeoLinks = (meta: Pick<SeoMeta, 'url' | 'alternates'>): React.ReactElement[] => {
  const { locale } = useRouter()
  const host = process.env.NEXT_PUBLIC_HOST

  if (!host) return []

  const links = [
    <link key="canonical" rel="canonical" href={`${host}${localePrefix(locale)}${meta.url}`} />,
  ]

  const translations = Object.entries(meta.alternates)
  if (translations.length < 2) return links

  translations.forEach(([alternateLocale, alternatePath]) => {
    const href = `${host}${localePrefix(alternateLocale)}${alternatePath}`
    links.push(<link key={alternateLocale} rel="alternate" hrefLang={alternateLocale} href={href} />)
    if (alternateLocale === SEO_DEFAULT_LOCALE) {
      links.push(<link key="x-default" rel="alternate" hrefLang="x-default" href={href} />)
    }
  })

  return links
}
